/*
 * Khung các trang ngoài lớp học: thanh trên cùng + nội dung + chân trang.
 * Dùng cho trang chủ, xem khoá, tham gia lớp, khoá học của tôi, cài đặt…
 */
import { Link, NavLink, Outlet, useLocation } from "react-router-dom"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet"
import { Icon } from "@/components/icon"
import { PageMotion, UserAvatar } from "@/components/page"
import { loginUrl, logoutUrl } from "@/lib/api"
import { cn } from "@/lib/utils"
import { useSession } from "./session"

const LINKS = [
  { to: "/", label: "Khóa học", end: true, auth: false },
  { to: "/my-courses", label: "Khóa học của tôi", auth: true },
  { to: "/feedback", label: "Góp ý", auth: true },
  { to: "/referrals", label: "Giới thiệu", auth: true },
]

export function Brand({ to = "/" }: { to?: string }) {
  return (
    <Link to={to} className="flex shrink-0 items-center gap-2.5">
      <img src="/brand/sutucon/mascots-2d/lion-happy.svg" alt="" className="size-10" />
      <span className="font-heading text-lg font-bold text-ink">Sư Tử Con</span>
    </Link>
  )
}

export function AccountMenu({ user, extra }: { user: any; extra?: { to: string; label: string; icon: Parameters<typeof Icon>[0]["name"] }[] }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex max-w-56 items-center gap-1.5 rounded-full border-[1.5px] border-edge bg-card p-1 pr-3 transition-colors hover:border-orange data-popup-open:border-orange">
        <UserAvatar user={user} size="sm" className="size-8" />
        <span className="hidden truncate text-sm font-semibold text-ink sm:inline">{user?.full_name || "Học viên Simba"}</span>
        <Icon name="chevron-down" className="size-4 text-muted-foreground" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-56">
        <DropdownMenuItem render={<Link to="/settings" />}>
          <Icon name="settings" /> Settings
        </DropdownMenuItem>
        <DropdownMenuItem render={<Link to="/family" />}>
          <Icon name="home" /> Thông tin của con
        </DropdownMenuItem>
        <DropdownMenuItem render={<Link to="/referrals" />}>
          <Icon name="share" /> Giới thiệu
        </DropdownMenuItem>
        {extra?.map((x) => (
          <DropdownMenuItem key={x.to} render={<Link to={x.to} />}>
            <Icon name={x.icon} /> {x.label}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem render={<a href={logoutUrl()} />} className="text-danger">
          <Icon name="x" /> Logout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function LoginButton() {
  return (
    <Button nativeButton={false} render={<a href={loginUrl(`${location.origin}/my-courses`)} />}>
      <Icon name="user" /> Đăng nhập bằng Gmail
    </Button>
  )
}

export function PublicLayout() {
  const { user, loading } = useSession()
  const { pathname } = useLocation()
  const links = LINKS.filter((l) => !l.auth || user)
  const navLink = (l: (typeof LINKS)[number], mobile?: boolean) => (
    <NavLink
      key={l.to}
      to={l.to}
      end={l.end}
      className={({ isActive }) =>
        cn(
          "rounded-full px-4 py-2 text-[15px] font-semibold whitespace-nowrap text-ink/75 transition-colors hover:bg-soft hover:text-ink",
          isActive && "bg-soft text-accent-foreground",
          mobile && "block py-3",
        )
      }
    >
      {l.label}
    </NavLink>
  )

  return (
    <div className="flex min-h-svh flex-col bg-sand">
      <header className="sticky top-0 z-30 border-b border-line bg-card/85 backdrop-blur">
        <div className="mx-auto flex h-16 w-full max-w-[1200px] items-center gap-3 px-4 sm:px-6">
          <Brand />
          <nav className="ml-4 hidden items-center gap-1 md:flex">{links.map((l) => navLink(l))}</nav>
          <div className="ml-auto flex items-center gap-2">
            {!loading && (user ? <AccountMenu user={user} /> : <span className="hidden sm:block"><LoginButton /></span>)}
            <Sheet>
              <SheetTrigger render={<Button variant="ghost" size="icon" className="md:hidden" aria-label="Mở menu" />}>
                <Icon name="list" />
              </SheetTrigger>
              <SheetContent side="right" className="w-72 bg-card p-5">
                <SheetTitle className="sr-only">Menu</SheetTitle>
                <nav className="mt-8 flex flex-col gap-1">{links.map((l) => navLink(l, true))}</nav>
                {!loading && !user && (
                  <div className="mt-4">
                    <LoginButton />
                  </div>
                )}
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>
      <main className="mx-auto w-full max-w-[1200px] flex-1 px-4 py-8 sm:px-6 md:py-12">
        <PageMotion motionKey={pathname}>
          <Outlet />
        </PageMotion>
      </main>
      <footer className="border-t-[1.5px] border-edge bg-paper">
        <div className="mx-auto flex w-full max-w-[1200px] flex-wrap items-center gap-x-5 gap-y-2 px-4 py-6 text-sm text-muted-foreground sm:px-6">
          <span className="mr-auto">Simba · Không gian học tập của Sư Tử Con</span>
          <a className="font-semibold text-orange-strong hover:underline" href="https://www.sutucon.com">
            sutucon.com
          </a>
        </div>
      </footer>
    </div>
  )
}
